import type { z } from 'zod'
import { afterValid } from '~/services/api/contracts/afterValid'
import { StoreUpdateTemporaryClosuresRequestSchema } from '~/services/api/generated'

interface StoreUpdateTemporaryClosuresContext {
  readonly existing_closure_ids: readonly number[]
  readonly now: string
}

interface ParsedClosure {
  readonly index: number
  readonly startsAt: number
  readonly endsAt: number
}

const schema = StoreUpdateTemporaryClosuresRequestSchema.strict()

export const StoreUpdateTemporaryClosures = (
  context: StoreUpdateTemporaryClosuresContext
): z.ZodType<z.output<typeof schema>> =>
  afterValid(schema, (value, issues) => {
    // UpdateStoreTemporaryClosuresRequest::withValidator (a): ownership
    const existing = new Set(context.existing_closure_ids)
    const foreign = value.temporary_closures.some(
      (closure) =>
        closure.id !== null &&
        closure.id !== undefined &&
        closure.id > 0 &&
        !existing.has(closure.id)
    )
    if (foreign) {
      issues.addIssue({
        code: 'custom',
        message: 'temporary_closures_ownership_invalid',
        path: ['temporary_closures']
      })
      return
    }

    // (b): each closure must end after it starts, and end in the future
    const now = Date.parse(context.now)
    const parsed: ParsedClosure[] = []
    value.temporary_closures.forEach((closure, index) => {
      const startsAt = Date.parse(closure.starts_at)
      const endsAt = Date.parse(closure.ends_at)
      if (Number.isNaN(startsAt) || Number.isNaN(endsAt)) return

      if (endsAt <= startsAt) {
        issues.addIssue({
          code: 'custom',
          message: 'temporary_closures_ends_before_starts',
          path: ['temporary_closures', index, 'ends_at']
        })
        return
      }
      if (endsAt <= now) {
        issues.addIssue({
          code: 'custom',
          message: 'temporary_closures_ends_in_past',
          path: ['temporary_closures', index, 'ends_at']
        })
        return
      }

      parsed.push({ endsAt, index, startsAt })
    })

    // (c): closures that passed (b) must not overlap, in start order
    const ordered = [...parsed].sort((left, right) => left.startsAt - right.startsAt)
    let previousEnd: number | null = null
    for (const closure of ordered) {
      if (previousEnd !== null && closure.startsAt < previousEnd) {
        issues.addIssue({
          code: 'custom',
          message: 'temporary_closures_overlap',
          path: ['temporary_closures']
        })
        return
      }
      previousEnd = closure.endsAt
    }
  })
